"use client";
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import debounce from "lodash/debounce";

import TextInput, { TextInputProps } from "@stadium/ui/src/components/form/TextInput";

import { ALPHA_NUMERIC_SEARCH_REGEX } from "@stadium/helpers/form/constants";
import Button from "@stadium/ui/src/components/button/styled-button";
import defaultStyles from "./main.module.scss";

export interface SearchBarProps extends Omit<TextInputProps, "onReset"> {
  /**
   * Resets the search query on change of this boolean to true
   */
  reset?: boolean;
  styles?: ModularStyles;
  id: string;
  name: string;
  /** Wait time before consecutive calls are made in ms
   *
   * Set `<=0` to disable
   *
   * `300` is default
   */
  debounceWaitTime?: number;
  baseContainerClass?: string;
  /**
   * This allows you to validate search input. Default regex is alphanumeric with spaces.
   * @see ALPHA_NUMERIC_SEARCH_REGEX `@stadium/helpers/form/constants`
   */
  inputRegex?: RegExp | string;
  wrapForm?: string;
  /**
   * Expects a boolean return in order to confirm resetting
   *
   * `false` = will not reset
   *
   *  `any other value` = will reset
   */
  canReset?: (e: React.MouseEvent<any, any>) => void | boolean | Promise<boolean>;
  /**
   * Runs only when user clicks on the cross icon
   */
  onReset?: (e: React.MouseEvent<any, any>) => void | boolean | Promise<boolean>;
  initialQuery?: string;
  iconClass?: string;
}

export type SearchBarRef = HTMLInputElement & {
  /**
   * This function resets the query and validates canReset (form props) before query is reset.
   */
  reset: (e?: Parameters<React.MouseEventHandler<any>>[0]) => unknown;
  /** This function sets the search textarea into focus */
  focus: () => unknown;
};

const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(
  (
    {
      id,
      name,
      placeholder = "",
      styles = defaultStyles,
      onChange,
      debounceWaitTime = 300,
      baseContainerClass = styles.formControlPadding,
      classes = "",
      iconClass = "",
      searchBarContainerClass="",
      onReset,
      canReset,
      initialQuery = "",
      inputRegex = ALPHA_NUMERIC_SEARCH_REGEX,
      ...rest
    },
    ref,
  ) => {
    const [query, setQuery] = useState(initialQuery);

    const inputRef = useRef<HTMLInputElement>();

    const debouncedOnChange = useCallback(
      debounceWaitTime > 0 ? debounce(onChange, debounceWaitTime) : onChange,
      [debounceWaitTime, onChange],
    );

    const reset: React.MouseEventHandler<any> = useCallback(
      async (e) => {
        if ((await canReset?.(e)) !== false) {
          setQuery("");
          onChange({ name, value: "", _e: e });
          onReset?.(e);
        }
      },
      [setQuery, onChange, onReset, canReset],
    );

    const focus = useCallback(() => inputRef?.current?.focus(), [inputRef]);

    useImperativeHandle(ref, () => ({ ...inputRef.current, reset, focus }), [reset, focus]);

    const handleQueryChange: TextInputProps["onChange"] = (...args) => {
      const [{ value }] = args;
      if (new RegExp(inputRegex).test(value)) {
        setQuery(value);
        debouncedOnChange(...args);
      }
    };

    return (
      <div className={`${styles.searchBarContainer} ${searchBarContainerClass}`}>
        <TextInput
          id={id}
          name={name}
          value={query}
          onChange={handleQueryChange}
          classes={`${baseContainerClass} ${classes}`}
          placeholder={placeholder}
          autoComplete="off"
          ref={inputRef}
          styles={styles !== defaultStyles ? styles : undefined}
          {...rest}
        />
        {query && (
          <Button
            className={`${styles.closeIcon} ${iconClass}`}
            onClick={reset}
            styleType="no-style"
            aria-label="Clear Search"
          />
        )}
      </div>
    );
  },
);

export default SearchBar;
